/**
 * Lightweight analytics tracker for x11.social
 * Sends events to /api/pulse endpoint
 */

interface PulseEvent {
  type: 'pageview' | 'event'
  data: {
    page?: string
    event?: string
    eventData?: Record<string, any>
    referrer?: string
    source?: string
    medium?: string
    campaign?: string
  }
  sessionId?: string
}

class Pulse {
  private sessionId: string
  private apiEndpoint = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/pulse`
  private respectDNT = true
  private initialReferrer: string
  
  constructor() {
    // Generate or retrieve session ID
    this.sessionId = this.getOrCreateSessionId()
    
    // Capture initial referrer (external source)
    this.initialReferrer = document.referrer
  }
  
  private getOrCreateSessionId(): string {
    const stored = sessionStorage.getItem('pulse_session')
    if (stored) return stored
    
    const sessionId = this.generateId()
    sessionStorage.setItem('pulse_session', sessionId)
    return sessionId
  }
  
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2)
  }
  
  private shouldTrack(): boolean {
    // Respect Do Not Track
    if (this.respectDNT && navigator.doNotTrack === '1') {
      return false
    }
    
    // Don't track in development by default (optional)
    // if (import.meta.env.DEV) return false
    
    return true
  }
  
  private async send(event: PulseEvent): Promise<void> {
    if (!this.shouldTrack()) return
    
    try {
      // Get auth token if available
      const token = localStorage.getItem('token')
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      // Fire and forget - don't await
      fetch(this.apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...event,
          sessionId: this.sessionId,
        }),
      }).catch(() => {
        // Silently fail - analytics should never break the app
      })
    } catch (error) {
      // Silently fail
    }
  }
  
  /**
   * Track a page view
   */
  pageview(page?: string): void {
    // For the first pageview, use the initial referrer
    // For subsequent pageviews, check if referrer is external
    const currentReferrer = document.referrer
    const isInternalNavigation = currentReferrer.includes(window.location.hostname)
    
    // Parse UTM parameters and other tracking params
    const urlParams = new URLSearchParams(window.location.search)
    const source = urlParams.get('utm_source') || urlParams.get('source') || urlParams.get('ref') || undefined
    const medium = urlParams.get('utm_medium') || urlParams.get('medium') || undefined
    const campaign = urlParams.get('utm_campaign') || urlParams.get('campaign') || undefined
    
    this.send({
      type: 'pageview',
      data: {
        page: page || window.location.pathname,
        referrer: isInternalNavigation ? this.initialReferrer : currentReferrer,
        source,
        medium,
        campaign,
      },
    })
  }
  
  /**
   * Track a custom event
   */
  event(eventName: string, eventData?: Record<string, any>): void {
    this.send({
      type: 'event',
      data: {
        event: eventName,
        eventData,
        page: window.location.pathname,
      },
    })
  }
  
  /**
   * Track specific user actions
   */
  trackSignUp(plan?: string): void {
    this.event('sign_up', { plan })
  }
  
  trackCallInitiated(): void {
    this.event('call_initiated')
  }
  
  trackPlanSelected(plan: string): void {
    this.event('plan_selected', { plan })
  }
  
  trackPhoneVerified(): void {
    this.event('phone_verified')
  }
  
  trackPostCreated(source: 'call' | 'draft'): void {
    this.event('post_created', { source })
  }
  
  /**
   * Onboarding flow tracking
   */
  trackOnboardingStarted(plan?: string): void {
    this.event('onboarding_started', { plan, step: 'registration' })
  }
  
  trackOnboardingStepViewed(step: 'registration' | 'verification' | 'payment' | 'success', plan?: string): void {
    this.event('onboarding_step_viewed', { step, plan })
  }
  
  trackOnboardingStepCompleted(step: 'registration' | 'verification' | 'payment' | 'success', plan?: string): void {
    this.event('onboarding_step_completed', { step, plan })
  }
  
  trackOnboardingError(step: string, error: string): void {
    this.event('onboarding_error', { step, error })
  }
  
  trackOnboardingAbandoned(step: string, plan?: string): void {
    this.event('onboarding_abandoned', { step, plan })
  }
  
  trackRegistrationStarted(method: 'x' | 'google' | 'email'): void {
    this.event('registration_started', { method })
  }
  
  trackRegistrationCompleted(method: 'x' | 'google' | 'email', userId?: string): void {
    this.event('registration_completed', { method, userId })
  }
  
  trackPhoneVerificationStarted(): void {
    this.event('phone_verification_started')
  }
  
  trackPhoneVerificationCodeSent(phoneNumber: string): void {
    // Hash phone number for privacy
    const hashedPhone = phoneNumber.slice(0, 3) + '***' + phoneNumber.slice(-2)
    this.event('phone_verification_code_sent', { hashedPhone })
  }
  
  trackPhoneVerificationCompleted(): void {
    this.event('phone_verification_completed')
  }
  
  trackPaymentStarted(plan: string, price: number): void {
    this.event('payment_started', { plan, price })
  }
  
  trackPaymentCompleted(plan: string, price: number, subscriptionId?: string): void {
    this.event('payment_completed', { plan, price, subscriptionId })
  }
  
  trackPaymentFailed(plan: string, error: string): void {
    this.event('payment_failed', { plan, error })
  }
  
  trackOnboardingCompleted(plan: string, totalDurationSeconds: number): void {
    this.event('onboarding_completed', { plan, totalDurationSeconds })
  }

  // Generic event tracking for custom events
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    this.event(eventName, properties)
  }
  
  // Get current session ID for demo calls
  getSessionId(): string {
    return this.sessionId
  }
}

// Export singleton instance
export const pulse = new Pulse()